import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './contexts/AuthContext';

// Componentes de páginas
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import DashboardPage from './pages/dashboard/DashboardPage';
import HomePage from './pages/home/HomePage';
import CategoryPage from './pages/category/CategoryPage';
import VideoPage from './pages/category/VideoPage';
import ARPage from './pages/category/ARPage';
import PortfolioPage from './pages/category/PortfolioPage';
import TestPage from './pages/category/TestPage';
import ProfilePage from './pages/profile/ProfilePage';
import CertificatesPage from './pages/certificates/CertificatesPage';
import NotificationsPage from './pages/certificates/NotificationsPage';


// Componentes de administración
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminUsers from './pages/admin/AdminUsers';
import AdminGroups from './pages/admin/AdminGroups';
import AdminContent from './pages/admin/AdminContent';
import AdminNotifications from './pages/admin/AdminNotifications';
import AdminReports from './pages/admin/AdminReports';

// Componentes de layout y protección
import Layout from './components/layout/Layout';
import ProtectedRoute from './components/auth/ProtectedRoute';
import AdminRoute from './components/auth/AdminRoute';
import { PageLoader } from './components/common';

// Configuración de TanStack Query (anteriormente React Query)
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 3,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      staleTime: 5 * 60 * 1000, // 5 minutos
      gcTime: 10 * 60 * 1000, // 10 minutos (antes era cacheTime)
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 1,
    },
  },
});

const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Router>
          <div className="App">
            <Routes>
              {/* Rutas públicas */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              
              {/* Rutas protegidas */}
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <Layout />
                  </ProtectedRoute>
                }
              >
                {/* Ruta principal redirige al dashboard */}
                <Route index element={<Navigate to="/home" replace />} /> {/* change dashboard to home */}
                
                {/* Dashboard y páginas principales */}
                <Route path="dashboard" element={<DashboardPage />} />
                <Route path="home" element={<HomePage />} />
                <Route path="profile" element={<ProfilePage />} />
                <Route path="certificates" element={<CertificatesPage />} />
                <Route path="notifications" element={<NotificationsPage />} />
                
                {/* Páginas de categorías */}
                <Route path="category/:categoryId" element={<CategoryPage />} />
                <Route path="category/:categoryId/video" element={<VideoPage />} />
                <Route path="category/:categoryId/ar" element={<ARPage />} />
                <Route path="category/:categoryId/portfolio" element={<PortfolioPage />} />
                <Route path="category/:categoryId/test" element={<TestPage />} />
                
                {/* Rutas de administración */}
                <Route
                  path="admin"
                  element={
                    <AdminRoute>
                      <AdminDashboard />
                    </AdminRoute>
                  }
                />
                <Route
                  path="admin/users"
                  element={
                    <AdminRoute>
                      <AdminUsers />
                    </AdminRoute>
                  }
                />
                <Route
                  path="admin/groups"
                  element={
                    <AdminRoute>
                      <AdminGroups />
                    </AdminRoute>
                  }
                />
                <Route
                  path="admin/content"
                  element={
                    <AdminRoute>
                      <AdminContent />
                    </AdminRoute>
                  }
                />
                <Route
                  path="admin/notifications"
                  element={
                    <AdminRoute>
                      <AdminNotifications />
                    </AdminRoute>
                  }
                />
                <Route
                  path="admin/reports"
                  element={
                    <AdminRoute>
                      <AdminReports />
                    </AdminRoute>
                  }
                />
              </Route>
              
              {/* Ruta 404 */}
              <Route path="*" element={<Navigate to="/home" replace />} />
            </Routes>
            
            {/* Notificaciones toast */}
            <Toaster
              position="top-right"
              toastOptions={{
                duration: 4000,
                style: {
                  background: '#363636',
                  color: '#fff',
                },
                success: {
                  duration: 3000,
                  iconTheme: {
                    primary: '#10b981',
                    secondary: '#fff',
                  },
                },
                error: {
                  duration: 5000,
                  iconTheme: {
                    primary: '#ef4444',
                    secondary: '#fff',
                  },
                },
              }}
            />
          </div>
        </Router>
      </AuthProvider>
    </QueryClientProvider>
  );
};

export default App;